import { createElement } from 'react';
import { Action, DATA } from '@wix/component-protocol/schema';
import {
  IntersectionVirtualManifestBuilder,
  ManifestPropertyFilter,
  ReducibleKey,
} from '@wix/editor-platform-manifest-services';
import { PanelTypeWithStructure } from '@wix/editor-elements-panel-service';
import { AutomaticPanel } from '../../../../ui/automaticPanel/AutomaticPanel';
import { PlatformContext } from '../types';
import { BaseActionPanelAPI } from './BaseActionPanelAPI';
import { IExecutionContext } from './types';

type DesignPanelMetadata = {
  namespace: 'cssProperties' | 'cssCustomProperties';
  panelType: Extract<PanelTypeWithStructure, 'design'>;
};

type SettingsPanelMetadata = {
  namespace: 'data';
  panelType: Extract<PanelTypeWithStructure, 'settings'>;
};

type ManagePanelMetadata = {
  namespace: 'data';
  panelType: Extract<PanelTypeWithStructure, 'manage'>;
};

type PanelMetadata =
  | DesignPanelMetadata
  | SettingsPanelMetadata
  | ManagePanelMetadata;

export class CustomActionPanelAPI extends BaseActionPanelAPI {
  readonly metadata: PanelMetadata;

  constructor(
    readonly action: Action,
    readonly actionName: string,
    readonly executionContext: IExecutionContext,
    readonly context: PlatformContext,
  ) {
    super();
    const namespace = this.executionContext.namespace;
    this.metadata =
      namespace === 'data'
        ? { namespace, panelType: this.#resolveDataPanelType() }
        : { namespace, panelType: 'design' };
  }

  #resolveDataPanelType(): Extract<
    PanelTypeWithStructure,
    'settings' | 'manage'
  > {
    const { itemKeys } = this.executionContext;
    if (!itemKeys || itemKeys.length === 0) {
      return 'settings';
    }

    const data = this.context.componentsAPI.components.getData(
      this.context.editorPointers,
    );

    const allArrayItems = itemKeys.every(
      (key) => data?.[key]?.dataType === DATA.DATA_TYPE.arrayItems,
    );

    return allArrayItems ? 'manage' : 'settings';
  }

  getContent() {
    return createElement(AutomaticPanel, {
      getHost: this.getHost,
      panelType: this.metadata.panelType,
    });
  }

  async getHostContext() {
    const filter: ManifestPropertyFilter = (
      manifestProp: ReducibleKey,
      key: string,
    ) => {
      if (manifestProp !== this.metadata.namespace) {
        return false;
      }

      const itemKeys = this.executionContext.itemKeys;
      if (!itemKeys || itemKeys.length === 0) {
        return true;
      }

      return itemKeys.includes(key);
    };

    return this.getInfraSDKHostContext({
      editorPointers: this.context.editorPointers,
      contextBuilder: (builder) =>
        builder.extend(IntersectionVirtualManifestBuilder).withFilters(filter),
    });
  }
}
